/**
 * Describes the Header's ResponsivenessTraits per page.
 * TODO: standardize types of ResponsivenessTraits
 */

export type PageName =
  | 'home#show'
  | 'single_work#show'
  | 'analytics_dashboard#index'
  | 'search#search'
  | 'profiles/works#summary'
  | 'library#index'
  | 'users#edit'
  | 'notification_messages#index'
  | 'discussions#index'
  | 'discussions#show'
  | 'upload_funnel#index'
  | 'friend_finder#google'
  | 'history#index'
  | 'message_threads#index'
  | 'premium_upgrade#index'
  | 'mentions#index'
  | 'grants#index'
  | 'readers#index'
  | 'saved_searches#index'
  | 'work_edit#edit'
  | 'by_tag#show_one'
  | 'people#index_by_research_interest'
  | 'universities#show'
  | 'static#about'
  | 'static#press'
  | 'static#hiring'
  | 'sales#new'
  | 'videos/video_player#show'
  | 'research#index'
  | 'courses/courses#index'
  | 'courses/courses#new'
  | 'recommendations#index';

type ResponsivenessTraits = {
  // The max width in px the Header is allowed to fill, always centered.
  maxWidth?: number; // defaults to 'none'
  // Reserves space whenever the Header is at full width, generally only horizontal padding is set.
  // Defaults to '0 12px 0 24px' (fullscreen), which will leave some additional space for the 'A' icon
  // To properly center the header, use only 1 horizontal value such as '0 12px'
  padding?: string; // defaults to '0 12px 0 24px'
};

const STANDARD_MAX_WIDTH = 1170; // px

// Gradually steps down the max width, until going fullscreen at mobile sizes
const stepDown = (width: number): ResponsivenessTraits => {
  if (width >= 1200) return { maxWidth: STANDARD_MAX_WIDTH, padding: '0 12px' };
  if (width >= 992) return { maxWidth: 970, padding: '0 12px' };
  if (width >= 768) return { maxWidth: 750, padding: '0 12px' };
  return {};
};

// A maxWidth, centered Header at larger screen sizes, becomes fullscreen at and below 'until'
const maxWidthUntilFullscreen = (
  width: number,
  until = 1200,
  maxWidth = STANDARD_MAX_WIDTH,
): ResponsivenessTraits => {
  if (width >= until) return { maxWidth: maxWidth, padding: '0 12px' };
  return {};
};

/**
 * In general, pages that are flush to the right or left edge should have a full-width header.
 * All other pages should use maxWidthUntilFullscreen.
 * The list below is full of exceptions to this rule of thumb,
 * because page width was never standardized and thus we have to accommodate subtle variations.
 * If undefined for the page, Header defaults to maxWidthUntilFullscreen.
 * This list is also being used to track the responsiveness status of each page.
 * Margin is instead set in the slim template to avoid vertical layout shifts after load.
 */
const getResponsivenessTraits = (
  pageName: PageName,
  width: number,
): ResponsivenessTraits => {
  switch (pageName) {
    case 'home#show':
      // TODO: home page sizing is broken!
      return { maxWidth: 1140 };
    case 'single_work#show':
      return {};
    case 'analytics_dashboard#index':
      // TODO: page sizing badly broken at any smaller size
      if (width > 1200)
        return { maxWidth: STANDARD_MAX_WIDTH, padding: '0 12px' };
      if (width >= 1000) return { maxWidth: 970, padding: '0 12px' };
      return {};
    case 'search#search':
      if (width > 750)
        return { maxWidth: STANDARD_MAX_WIDTH, padding: '0 56px' };
      return {};
    case 'profiles/works#summary':
      return stepDown(width);
    case 'library#index':
      return {};
    case 'users#edit': // aka settings, TODO need a better pageName for this
      // TODO: page broken at small sizes
      return { padding: '0 10%' };
    case 'notification_messages#index':
      // TODO: also broken at small size
      return stepDown(width);
    case 'discussions#index':
      if (width > 900) return { padding: '0 12%' };
      return {};
    case 'discussions#show':
      return {};
    case 'upload_funnel#index':
      return maxWidthUntilFullscreen(width, 1170, 1140);
    case 'friend_finder#google':
      if (width >= 1200)
        return { maxWidth: STANDARD_MAX_WIDTH, padding: '0 15px' };
      if (width > 991) return { maxWidth: 970, padding: '0 15px' };
      if (width > 767) return { maxWidth: 750, padding: '0 15px' };
      return { padding: '0 15px' };
    case 'history#index':
      // aka reading history, this should just be the new library now!
      return stepDown(width);
    case 'premium_upgrade#index':
      // TODO: does this change depending on which marketing page?
      return { maxWidth: STANDARD_MAX_WIDTH };
    case 'mentions#index':
      return maxWidthUntilFullscreen(width);
    case 'grants#index':
      return maxWidthUntilFullscreen(width);
    case 'readers#index':
      return maxWidthUntilFullscreen(width);
    case 'saved_searches#index':
      return maxWidthUntilFullscreen(width);
    case 'work_edit#edit':
      return stepDown(width);
    case 'by_tag#show_one':
    case 'people#index_by_research_interest':
      return stepDown(width);
    case 'universities#show':
      return stepDown(width);
    case 'static#about':
      return { maxWidth: 1140 };
    case 'static#press':
      return stepDown(width);
    case 'static#hiring':
      return maxWidthUntilFullscreen(width);
    case 'sales#new':
      return maxWidthUntilFullscreen(width);
    case 'videos/video_player#show':
      return {};
    case 'research#index':
      return maxWidthUntilFullscreen(width, 1440, 1420);
    case 'courses/courses#new':
      return {};
    case 'recommendations#index':
      return {};
    default:
      return maxWidthUntilFullscreen(width); // max width of STANDARD_MAX_WIDTH, fullscreen at smaller sizes
  }
};

export default getResponsivenessTraits;
